import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import Chart from 'chart.js/auto';

import { ErrorService } from '../../../../../shared/services/error-service';
import { ToastService } from '../../../../../shared/toast/toast.service';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { ImportAnnexureService } from '../../../../../shared/services/import-annexure-service';
import { DialogService } from '../../../../../shared/controls/message-dialog/message-dialog.service';
import { LoadingService } from '../../../../../shared/services/loading.service';
import { AnnexureBatchResult, AnnexureService, AnnexureTotalsResult, SampleAnnexureDataResult } from '../../../../../../service/proxy';
import { buildFilteredErrors, formatErrorValue } from '../../../../../shared/helpers/import-error-helper';

@Component({
    selector: 'annexure-import-data',
    templateUrl: './annexure-import-data.component.html',
    styleUrls: ['./annexure-import-data.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        AnnexureService,
    ]
})
export class AnnexureImportDataComponent implements OnInit, OnDestroy, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;

    public batch: AnnexureBatchResult = new AnnexureBatchResult();
    public totals: AnnexureTotalsResult = new AnnexureTotalsResult();
    public sampleData: SampleAnnexureDataResult = new SampleAnnexureDataResult();

    public importInProgress: boolean = false;
    public importProgress: number = 0;
    public importCompleted: boolean = false;
    public importErrors: any[] = []
    public showErrors = false;
    public totalErrorCount: number = 0;
    public errorRowCount: number = 0;
    public totalRows: number = 0;
    public rowsImported: number = 0;

    public showData = false;
    public filteredErrors: any[] = [];

    public headers: string[] = [];
    public loading: boolean = false;
    public loadingTotals: boolean = false;

    public hasCheckedMappedColumns: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private annexureService: AnnexureService,
        private importAnnexureService: ImportAnnexureService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private dialogService: DialogService,
        private loadingService: LoadingService
    ) {}

    ngOnInit(): void {
    }

    ngOnDestroy(): void {
        if (this.importAnnexureService) {
            this.importAnnexureService.stopListening();
        }
    }

    processErrors(): void {
        this.filteredErrors = buildFilteredErrors(this.importErrors, 10);
    }

    formatErrorValue(value: any): string {
        return formatErrorValue(value, 120);
    }

    onActivate(): void {
        this.hasCheckedMappedColumns = false;

        this.route.paramMap.subscribe(params => {
            this.batchId = Number(params.get('id'));

            if (this.batchId > 0) {

                const mappingsChangedKey = `annexure_mappings_changed_${this.batchId}`;
                const mappingChanged = sessionStorage.getItem(mappingsChangedKey) === 'true';

                this.hasCheckedMappedColumns = true;

                if (mappingChanged) {
                    sessionStorage.removeItem(mappingsChangedKey);
                    this.deleteExistingDataAndImport();
                }
                else {
                    this.loadExistingBatchData();
                }
            }
        });
    }

    private loadExistingBatchData(): void {
        this.importInProgress = true;
        this.cdr.detectChanges();

        this.annexureService.calculateAnnexureTotals(this.batchId)
            .subscribe({
                next: (response) => {
                    this.totals = response;

                    if (response.totalRowsImported > 0) {
                        // Data exists - show results panel
                        this.importCompleted = true;
                        this.importProgress = 100;
                        this.importInProgress = false;
                        this.cdr.detectChanges();

                        setTimeout(() => {
                            this.initializeChart();
                        }, 100);

                        this.toastService.show('Loaded existing batch data.', 'Info');
                    }
                    else {
                        // No data imported yet - run the import
                        this.importInProgress = false;
                        this.cdr.detectChanges();
                        
                        this.toastService.show('No data imported yet. Starting import...', 'Info');
                        
                        setTimeout(() => {
                            this.deleteExistingDataAndImport();
                        }, 500);
                    }
                },
                error: (error) => {
                    this.importInProgress = false;
                    this.errorService.handleError(error);
                    this.toastService.show('Failed to load batch data.', 'Error');
                    this.cdr.detectChanges();
                }
            });
    }

    private deleteExistingDataAndImport(): void {
        this.toastService.show('Deleting existing data and re-importing...', 'Info');

        this.importCompleted = false;
        this.importProgress = 0;
        this.importErrors = [];
        this.filteredErrors = [];
        this.showErrors = false;
        this.showData = false;
        this.sampleData = new SampleAnnexureDataResult();
        this.totals = new AnnexureTotalsResult();

        this.cdr.detectChanges();

        this.annexureService.deleteAnnexureDataByBatchId(this.batchId)
            .subscribe({
                next: () => {
                    this.startImport();
                },
                error: (error) => {
                    this.importInProgress = false;
                    this.importCompleted = false;
                    this.cdr.detectChanges();
                    this.errorService.handleError(error);
                    this.toastService.show('Failed to delete existing annexure data.', 'Error');
                }
            });
    }

    private startImport(): void {
        this.importInProgress = true;
        this.importCompleted = false;
        this.cdr.detectChanges();

        this.importAnnexureService.startListening(this.batchId, (entry) => {
            if (entry) {
                this.importProgress = entry.progress;
                this.importErrors = entry.errors || [];
                this.totalErrorCount = entry.totalErrorCount || entry.errors?.length || 0;
                this.errorRowCount = entry.errorRowCount || 0;
                this.totalRows = entry.totalRows || 0;
                this.rowsImported = entry.rowsImported || 0;
                this.cdr.detectChanges();
            }
            else {
                this.toastService.show('No progress data received.', 'Info');
            }

            if (this.importProgress === 100 || entry.message === 'Import completed.') {
                this.importInProgress = false;
                this.importErrors = entry.errors || [];
                this.totalErrorCount = entry.totalErrorCount || entry.errors?.length || 0;
                this.errorRowCount = entry.errorRowCount || 0;
                this.totalRows = entry.totalRows || 0;
                this.rowsImported = entry.rowsImported || 0;
                this.showErrors = this.importErrors.length > 0;
                this.processErrors();
                this.cdr.detectChanges();

                setTimeout(() => {
                    this.importInProgress = false;
                    this.importCompleted = true;

                    const batchImportedKey = `annexure_batch_imported_${this.batchId}`;
                    sessionStorage.setItem(batchImportedKey, 'true');

                    this.cdr.detectChanges();
                    this.getTotals();
                    this.showData = false;
                }, 3000);
            }
        },
        () => {
            this.importInProgress = false;
            this.importCompleted = false;
            this.cdr.detectChanges();
            this.toastService.show('Failed to establish connection to server. Please try again.', 'Error');
        });
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            this.annexureService.calculateAnnexureTotals(this.batchId).subscribe({
                next: () => {
                    this.toastService.show('Annexure batch completed successfully.', 'Success');

                    observer.next(true);
                    observer.complete();
                },
                error: (error) => {
                    if (error.status === 404) {
                        this.toastService.show(`Annexure batch with ID ${this.batchId} not found.`, 'Error');
                    }
                    else {
                        this.toastService.show('An error occurred while completing the annexure batch. Please try again.', 'Error');
                        this.errorService.handleError(error);
                    }

                    observer.next(false);
                    observer.complete();
                },
            });
        });
    }

    onPrevious(): Observable<boolean> {
        // Reset completion flag so returning to this step will re-import
        this.importCompleted = false;
        return scheduled([true], asyncScheduler);
    }

    isValid(): boolean {
         return this.importErrors.length === 0;
    }


    getTotals(): void {
        this.loadingTotals = true;
        this.loadingService.show();
        this.cdr.detectChanges();

        this.annexureService.calculateAnnexureTotals(this.batchId)
            .subscribe({
                next: (response) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();
                    this.totals = response;
                    this.importCompleted = true;
                    this.importProgress = 100;
                    this.importInProgress = false;
                    this.cdr.detectChanges();

                    setTimeout(() => {
                        this.initializeChart();
                    }, 100);
                },
                error: (error) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();

                    if (error.status === 400) {
                        this.toastService.show('Failed to fetch import batch data. Please try again.', 'Error');
                    }
                    else if (error.status === 404) {
                        this.toastService.show('Not found. Please try again.', 'Info');
                    }
                    else {
                        this.toastService.show('Failed to load batch data. Please try again.', 'Error');
                        this.errorService.handleError(error);
                    }
                }
            });
    }

    initializeChart(): void {
        const ctx = document.getElementById('approvalChart') as HTMLCanvasElement;

        if (ctx) {
            this.cdr.detectChanges();

            new Chart(ctx, {
                type: "pie",
                data: {
                    labels: ["Approved", "Requested"],
                    datasets: [{
                        data: [this.totals.approvedPercentage, 100 - this.totals.approvedPercentage],
                        backgroundColor: ["#4682B4", "#002c77"],
                        borderWidth: 1
                    }]
                },
                options: {
                    responsive: true,
                    plugins: {
                        legend: { display: true, position: "top", align: "start" }
                    }
                }
            });
        }
    }

    toggleSampleData(): void {
        if (!this.showData) {
            if (!this.sampleData || !Array.isArray(this.sampleData.sampleRows) || this.sampleData.sampleRows.length === 0)
            {
                this.getSampleData();
            }
            else {
                this.showData = true;
                this.cdr.detectChanges();
            }
        }
        else {
            this.showData = false;
            this.cdr.detectChanges();
        }
    }

    getSampleData(): void {
        this.loading = true;
        this.annexureService.getAnnexureSampleData(this.batchId)
            .subscribe({
                next: (response) => {
                    this.sampleData = response;

                    if (response.sampleRows && response.sampleRows.length > 0) {
                        this.headers = Object.keys(response.sampleRows[0]);
                    }

                    this.showData = true;
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    if (error.status === 400) {
                        this.toastService.show('Bad request. Please try again.', 'Info');
                    }
                    else if (error.status === 404) {
                        this.toastService.show('No sample data found for this batch.', 'Info');
                    }
                    else {
                        this.toastService.show('Failed to fetch sample batch data. Please try again.', 'Error');
                        this.errorService.handleError(error);
                    }
                },
                complete: () => {
                    this.loading = false;
                    this.cdr.detectChanges();
                }
            });
    }

    formatHeader(header: string): string {
        let formatted = header.replace(/_/g, ' ');

        formatted = formatted.replace(/([A-Z])/g, ' $1');
        formatted = formatted.trim();
        formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);

        return formatted;
    }

    formatNumber(value: number): string {
        if (!value) {
            return '0';
        }

        return value.toLocaleString('en-US');
    }

    toggleError() {
        this.showErrors = !this.showErrors;
    }

    deleteAnnexureBatch(): void {
        if (!this.batchId) {
            this.toastService.show('No batch selected to delete.', 'Error');
            return;
        }
        this.dialogService.show('Delete Pending Batch', 'Are you sure you want to delete this batch?', 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.annexureService.deletePendingAnnexureBatch(this.batchId)
                        .subscribe({
                            next: () => {
                                this.toastService.show('Annexure batch deleted successfully.', 'success');
                                this.router.navigate(['/annexure-create']);
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            }
                        }
                    );
                }
            }
        );
    }
}
